/**
 * Simple Event Bus Service
 * 
 * Usage:
 * - Subscribe: this.eventsService.subscribe('myEvent', (data) => console.log(data));
 * - Publish: this.eventsService.publish('myEvent', { some: 'data' });
 * - Unsubscribe: subscription.unsubscribe();
*/
import { Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class EventsService {
    // Store event channels by topic name
    // Each channel can emit any type of data
    private channels = new Map<string, Subject<any>>();
    
    // Track subscriber counts manually since RxJS doesn't expose this reliably
    private subscriberCounts = new Map<string, number>();

    /**
     * Subscribe to an event topic
     * @param topic - The event name to listen for
     * @param callback - Function to call when event is published
     * @returns Subscription that can be unsubscribed
     */
    public subscribe(topic: string, callback: (data: any) => void): Subscription {
        // Get or create channel for this topic
        if (!this.channels.has(topic)) {
            this.channels.set(topic, new Subject<any>());
            this.subscriberCounts.set(topic, 0);
        }

        const channel = this.channels.get(topic)!;
        
        // Increment subscriber count
        const currentCount = this.subscriberCounts.get(topic) || 0;
        this.subscriberCounts.set(topic, currentCount + 1);
        
        const subscription = channel.subscribe(callback);
        
        // Decrement count when unsubscribed
        const originalUnsubscribe = subscription.unsubscribe.bind(subscription);
        subscription.unsubscribe = () => {
            const count = this.subscriberCounts.get(topic) || 0;
            this.subscriberCounts.set(topic, Math.max(0, count - 1));
            originalUnsubscribe();
        };
        
        return subscription;
    }

    /**
     * Publish data to all subscribers of a topic
     * @param topic - The event name to publish to
     * @param data - Optional data to send to subscribers
     */
    public publish(topic: string, data?: any): void {
        const channel = this.channels.get(topic);
        
        if (!channel) {
            // console.warn(`[EventsService] No subscribers for topic: ${topic}`);
            return;
        }

        // console.log(`[EventsService] Publishing to ${topic}:`, data);
        channel.next(data);
    }

    /**
     * Unsubscribe all listeners from a specific topic
     * @param topic - The event name to unsubscribe from
     */
    public unsubscribe(topic: string): void {
        const channel = this.channels.get(topic);
        
        if (!channel) {
            return;
        }

        // Complete the subject to unsubscribe all listeners
        channel.complete();
        
        // Remove from tracking
        this.channels.delete(topic);
        this.subscriberCounts.delete(topic);
    }

    /**
     * Check if a topic has any subscribers
     * @param topic - The event name to check
     * @returns True if there are subscribers
     */
    public hasSubscribers(topic: string): boolean {
        const channel = this.channels.get(topic);
        if (!channel) {
            return false;
        }
        
        // Use 'observed' property (non-deprecated) or check our manual count
        return channel.observed || (this.subscriberCounts.get(topic) || 0) > 0;
    }

    /**
     * Get the number of subscribers for a topic
     * @param topic - The event name to check
     * @returns Number of active subscribers
     */
    public getSubscriberCount(topic: string): number {
        return this.subscriberCounts.get(topic) || 0;
    }

    /**
     * Destroy a specific topic channel
     * Completes the observable and removes all subscribers
     * @param topic - The event name to destroy
     */
    public destroy(topic: string): void {
        const channel = this.channels.get(topic);
        
        if (!channel) {
            return;
        }

        // console.log(`[EventsService] Destroying channel: ${topic}`);
        channel.complete();
        this.channels.delete(topic);
        this.subscriberCounts.delete(topic);
    }

    /**
     * Destroy all event channels
     * Useful for cleanup when app is shutting down
     */
    public destroyAll(): void {
        // console.log('[EventsService] Destroying all channels');
        
        this.channels.forEach((channel, topic) => {
            channel.complete();
        });
        
        this.channels.clear();
        this.subscriberCounts.clear();
    }
}